import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Article as ArticleIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Email as EmailIcon,
  Instagram as InstagramIcon,
  Language as WebIcon,
  PhotoCamera as SocialIcon,
  PlayCircle as VideoIcon,
  Publish as PublishIcon,
  Refresh as RefreshIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import service, {
  Conteudo,
  ConteudoPayload,
  ConteudoStats,
  EstadoConteudo,
  SocialDiagnostico,
  TipoConteudo,
} from '../../services/marketing-conteudo.service';
import { PageHeader, StatCard, FilterBar, EmptyState } from '../../components/Marketing';
import {
  ScheduleOutlined,
  DraftsOutlined,
  CheckCircleOutline,
  VisibilityOutlined,
  CalendarMonthOutlined,
} from '@mui/icons-material';

const TIPO_LABELS: Record<TipoConteudo, string> = {
  post_social: 'Post Social',
  video: 'Vídeo',
  artigo: 'Artigo',
  email_blast: 'Email Blast',
};

const TIPO_ICONS: Record<TipoConteudo, any> = {
  post_social: SocialIcon,
  video: VideoIcon,
  artigo: ArticleIcon,
  email_blast: EmailIcon,
};

const ESTADO_COLORS: Record<EstadoConteudo, 'default' | 'info' | 'success' | 'warning'> = {
  rascunho: 'default',
  agendado: 'info',
  publicado: 'success',
  arquivado: 'warning',
};

const CATEGORIAS = ['pagamentos', 'cashback', 'food', 'events', 'referral', 'fidelidade', 'tutorial', 'lançamento'];

const EMPTY: ConteudoPayload = {
  titulo: '',
  tipo: 'post_social',
  plataforma: 'instagram',
  estado: 'rascunho',
  descricao: '',
  categoria: 'pagamentos',
};

// data_agendada é hora de parede de CV (mostrar em UTC); data_publicada é instante (hora de CV).
const formatDT = (v?: string | null) => (v ? new Date(v).toLocaleString('pt-PT', { timeZone: 'Atlantic/Cape_Verde' }) : '—');
const formatDTParede = (v?: string | null) => (v ? new Date(v).toLocaleString('pt-PT', { timeZone: 'UTC' }) : '—');

export default function ConteudoPage() {
  const [rows, setRows] = useState<Conteudo[]>([]);
  const [stats, setStats] = useState<ConteudoStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState<{
    msg: string;
    severity: 'success' | 'error' | 'warning';
  } | null>(null);
  const [publicandoId, setPublicandoId] = useState<number | null>(null);
  const [diagnostico, setDiagnostico] = useState<SocialDiagnostico | null>(null);

  const [tipoF, setTipoF] = useState('');
  const [estadoF, setEstadoF] = useState('');
  const [search, setSearch] = useState('');

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Conteudo | null>(null);
  const [form, setForm] = useState<ConteudoPayload>(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [list, s, d] = await Promise.all([
        service.list({ tipo: tipoF, estado: estadoF, search }),
        service.stats().catch(() => null),
        // O diagnóstico é informativo: se falhar, a página funciona na mesma.
        service.diagnostico().catch(() => null),
      ]);
      setRows(list);
      setStats(s);
      setDiagnostico(d);
    } catch (e: any) {
      setToast({ msg: e?.message || 'Erro', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [tipoF, estadoF, search]);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY);
    setOpen(true);
  };

  const openEdit = (c: Conteudo) => {
    setEditing(c);
    setForm({
      titulo: c.titulo,
      tipo: c.tipo,
      plataforma: c.plataforma || '',
      descricao: c.descricao || '',
      categoria: c.categoria || '',
      data_agendada: c.data_agendada?.slice(0, 16) || '',
      data_publicada: c.data_publicada?.slice(0, 16) || '',
      url_publicacao: c.url_publicacao || '',
      url_midia: c.url_midia || '',
      thumbnail: c.thumbnail || '',
      duracao_segundos: c.duracao_segundos ?? undefined,
      estado: c.estado,
      views: c.views,
      cliques: c.cliques,
      engagement: c.engagement,
      notas: c.notas || '',
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.titulo.trim()) {
      setToast({ msg: 'Título obrigatório', severity: 'error' });
      return;
    }
    setSaving(true);
    try {
      if (editing) await service.update(editing.id, form);
      else await service.create(form);
      setToast({ msg: editing ? 'Atualizado' : 'Criado', severity: 'success' });
      setOpen(false);
      await load();
    } catch (e: any) {
      setToast({ msg: e?.message || 'Erro', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const publicar = async (c: Conteudo) => {
    const alvos = c.plataforma || 'Facebook e Instagram';
    if (
      !window.confirm(
        `Publicar "${c.titulo}" em ${alvos}?\n\n` +
          'Isto publica já, de verdade, na página da Pagali.',
      )
    ) {
      return;
    }

    setPublicandoId(c.id);
    try {
      const r = await service.publicar(c.id);
      const onde = [r.facebook && 'Facebook', r.instagram && 'Instagram']
        .filter(Boolean)
        .join(' e ');
      setToast({
        msg: r.errors?.length
          ? `Publicado em ${onde}. Avisos: ${r.errors.join(' | ')}`
          : `Publicado em ${onde}`,
        severity: r.errors?.length ? 'warning' : 'success',
      });
      await load();
    } catch (e: any) {
      setToast({
        msg: e?.response?.data?.error || e?.message || 'Erro ao publicar',
        severity: 'error',
      });
    } finally {
      setPublicandoId(null);
    }
  };

  /** Para conteúdo publicado à mão, ou em plataformas que não integramos. */
  const marcarPublicado = async (c: Conteudo) => {
    const url = window.prompt('URL de publicação (opcional):', c.url_publicacao || '');
    if (url === null) return;
    try {
      await service.marcarPublicado(c.id, url || undefined);
      setToast({ msg: 'Marcado como publicado', severity: 'success' });
      await load();
    } catch (e: any) {
      setToast({ msg: e?.response?.data?.error || 'Erro', severity: 'error' });
    }
  };

  const atualizarMetricas = async (c: Conteudo) => {
    try {
      const m = await service.atualizarMetricas(c.id);
      setToast({
        msg: `${m.views} impressões · ${m.engagement} interacções · ${m.cliques} cliques`,
        severity: 'success',
      });
      await load();
    } catch (e: any) {
      setToast({ msg: e?.response?.data?.error || 'Erro', severity: 'error' });
    }
  };

  const remove = async (c: Conteudo) => {
    if (!window.confirm(`Eliminar "${c.titulo}"?`)) return;
    try {
      await service.remove(c.id);
      setToast({ msg: 'Eliminado', severity: 'success' });
      await load();
    } catch (e: any) {
      setToast({ msg: e?.message || 'Erro', severity: 'error' });
    }
  };

  // Publicar exige token, uma página de destino e as permissões da Graph API.
  // O Instagram vem por acréscimo — publicar só no Facebook já é publicar.
  const podePublicar =
    !!diagnostico?.token_configurado &&
    !!diagnostico?.page_id &&
    !diagnostico?.em_falta?.includes('pages_manage_posts');

  return (
    <Box>
      <PageHeader
        title="Calendário de Conteúdo"
        subtitle="Posts em redes sociais, vídeos educativos e comunicações"
        icon={<CalendarMonthOutlined />}
        actions={
          <>
            <Button startIcon={<RefreshIcon />} onClick={load}>Atualizar</Button>
            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>Novo Conteúdo</Button>
          </>
        }
      />

      <Alert severity="info" sx={{ mb: 2 }}>
        <b>Passos 11-12</b>: planeie a sequência semanal nas redes sociais + série de vídeos educativos
        (Como pagar, Como ganhar cashback, Como subir de nível). Estados: rascunho → agendado → publicado.
      </Alert>

      {diagnostico && !podePublicar && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          A publicação automática ainda não está operacional
          {!diagnostico.token_configurado && ' — falta o token do Meta'}
          {diagnostico.token_configurado && !diagnostico.page_id && ' — falta o page_id'}
          {!!diagnostico.em_falta?.length &&
            ` — faltam permissões: ${diagnostico.em_falta.join(', ')}`}
          . Até lá, usa <b>Marcar como publicado</b> depois de publicares à mão.
        </Alert>
      )}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 3, mb: 3 }}>
        <StatCard icon={<DraftsOutlined />} label="Rascunhos" value={stats?.rascunhos ?? '—'} color="secondary" />
        <StatCard icon={<ScheduleOutlined />} label="Agendados" value={stats?.agendados ?? '—'} color="info" />
        <StatCard icon={<CheckCircleOutline />} label="Publicados" value={stats?.publicados ?? '—'} color="success" />
        <StatCard
          icon={<VisibilityOutlined />}
          label="Views totais"
          value={(stats?.views_total ?? 0).toLocaleString('pt-PT')}
          color="warning"
        />
      </Box>

      <FilterBar>
        <TextField
          size="small"
          label="Pesquisar"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <FormControl size="small">
          <InputLabel>Tipo</InputLabel>
          <Select label="Tipo" value={tipoF} onChange={(e) => setTipoF(e.target.value)}>
            <MenuItem value="">Todos</MenuItem>
            {Object.entries(TIPO_LABELS).map(([k, v]) => (
              <MenuItem key={k} value={k}>{v}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small">
          <InputLabel>Estado</InputLabel>
          <Select label="Estado" value={estadoF} onChange={(e) => setEstadoF(e.target.value)}>
            <MenuItem value="">Todos</MenuItem>
            <MenuItem value="rascunho">Rascunho</MenuItem>
            <MenuItem value="agendado">Agendado</MenuItem>
            <MenuItem value="publicado">Publicado</MenuItem>
            <MenuItem value="arquivado">Arquivado</MenuItem>
          </Select>
        </FormControl>
      </FilterBar>

      <Paper>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Conteúdo</TableCell>
                <TableCell>Plataforma</TableCell>
                <TableCell>Categoria</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell>Agendado/Publicado</TableCell>
                <TableCell align="right">Métricas</TableCell>
                <TableCell align="right">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} sx={{ border: 0 }}>
                    <EmptyState
                      icon={<CalendarMonthOutlined />}
                      title="Sem conteúdo planeado"
                      description="Comece o calendário editorial com posts, vídeos educativos ou artigos. Rastrear datas e métricas num único sítio."
                      action={
                        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
                          Novo Conteúdo
                        </Button>
                      }
                    />
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((c) => {
                  const Icon = TIPO_ICONS[c.tipo];
                  return (
                    <TableRow key={c.id} hover>
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          {c.thumbnail ? (
                            <Avatar src={c.thumbnail} variant="rounded" sx={{ width: 44, height: 44 }} />
                          ) : (
                            <Avatar variant="rounded" sx={{ width: 44, height: 44 }}>
                              <Icon />
                            </Avatar>
                          )}
                          <Box>
                            <Typography variant="body2" fontWeight={500}>{c.titulo}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {TIPO_LABELS[c.tipo]}
                              {c.duracao_segundos ? ` · ${Math.floor(c.duracao_segundos / 60)}:${String(c.duracao_segundos % 60).padStart(2, '0')}` : ''}
                            </Typography>
                          </Box>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        {c.plataforma ? <Chip size="small" label={c.plataforma} variant="outlined" /> : '—'}
                      </TableCell>
                      <TableCell>{c.categoria || '—'}</TableCell>
                      <TableCell>
                        <Chip size="small" label={c.estado} color={ESTADO_COLORS[c.estado]} />
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" display="block">
                          {c.data_agendada ? `📅 ${formatDTParede(c.data_agendada)}` : ''}
                        </Typography>
                        <Typography variant="caption" display="block" color="success.main">
                          {c.data_publicada ? `✓ ${formatDT(c.data_publicada)}` : ''}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        {c.estado === 'publicado' ? (
                          <Typography variant="caption">
                            👁 {c.views} · 🔗 {c.cliques} · 💬 {c.engagement}
                          </Typography>
                        ) : (
                          <Typography variant="caption" color="text.secondary">—</Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {c.url_publicacao && (
                          <Tooltip title="Abrir publicação">
                            <IconButton size="small" onClick={() => window.open(c.url_publicacao!, '_blank')}>
                              <WebIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {c.estado !== 'publicado' && c.estado !== 'arquivado' && (
                          <>
                            <Tooltip title="Publicar agora nas redes">
                              <span>
                                <IconButton
                                  size="small"
                                  color="success"
                                  onClick={() => publicar(c)}
                                  disabled={publicandoId === c.id}
                                >
                                  {publicandoId === c.id ? (
                                    <CircularProgress size={18} />
                                  ) : (
                                    <PublishIcon fontSize="small" />
                                  )}
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="Marcar como publicado (sem publicar)">
                              <IconButton size="small" onClick={() => marcarPublicado(c)}>
                                <CheckIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                        {c.estado === 'publicado' && (
                          <Tooltip title="Actualizar métricas">
                            <IconButton size="small" onClick={() => atualizarMetricas(c)}>
                              <RefreshIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Editar">
                          <IconButton size="small" onClick={() => openEdit(c)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Eliminar">
                          <IconButton size="small" color="error" onClick={() => remove(c)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>{editing ? 'Editar Conteúdo' : 'Novo Conteúdo'}</DialogTitle>
        <DialogContent dividers sx={{ py: 3 }}>
          <Stack spacing={3}>
            <TextField
              fullWidth
              label="Título"
              placeholder="Ex: Como ganhar cashback no Pagali"
              value={form.titulo}
              onChange={(e) => setForm({ ...form, titulo: e.target.value })}
              autoFocus
            />

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 3 }}>
              <FormControl fullWidth>
                <InputLabel>Tipo</InputLabel>
                <Select
                  label="Tipo"
                  value={form.tipo || 'post_social'}
                  onChange={(e) => setForm({ ...form, tipo: e.target.value as TipoConteudo })}
                >
                  {Object.entries(TIPO_LABELS).map(([k, v]) => (
                    <MenuItem key={k} value={k}>{v}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                fullWidth
                label="Plataforma"
                placeholder="instagram, tiktok..."
                value={form.plataforma || ''}
                onChange={(e) => setForm({ ...form, plataforma: e.target.value })}
              />
              <FormControl fullWidth>
                <InputLabel>Categoria</InputLabel>
                <Select
                  label="Categoria"
                  value={form.categoria || ''}
                  onChange={(e) => setForm({ ...form, categoria: e.target.value })}
                >
                  <MenuItem value="">—</MenuItem>
                  {CATEGORIAS.map((c) => (
                    <MenuItem key={c} value={c}>{c}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <TextField
              fullWidth
              multiline
              minRows={3}
              label="Descrição / Caption"
              placeholder="Texto que vai no post. Para vídeo: roteiro curto."
              value={form.descricao || ''}
              onChange={(e) => setForm({ ...form, descricao: e.target.value })}
            />

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
              <TextField
                fullWidth
                type="datetime-local"
                label="Agendado para"
                InputLabelProps={{ shrink: true }}
                value={form.data_agendada || ''}
                onChange={(e) => setForm({ ...form, data_agendada: e.target.value, estado: e.target.value ? 'agendado' : form.estado })}
              />
              <FormControl fullWidth>
                <InputLabel>Estado</InputLabel>
                <Select
                  label="Estado"
                  value={form.estado || 'rascunho'}
                  onChange={(e) => setForm({ ...form, estado: e.target.value as EstadoConteudo })}
                >
                  <MenuItem value="rascunho">Rascunho</MenuItem>
                  <MenuItem value="agendado">Agendado</MenuItem>
                  <MenuItem value="publicado">Publicado</MenuItem>
                  <MenuItem value="arquivado">Arquivado</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <TextField
              fullWidth
              label="URL da publicação"
              placeholder="https://instagram.com/p/..."
              value={form.url_publicacao || ''}
              onChange={(e) => setForm({ ...form, url_publicacao: e.target.value })}
            />

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: form.tipo === 'video' ? '1fr 1fr 1fr' : '1fr 1fr' }, gap: 3 }}>
              <TextField
                fullWidth
                label="URL da mídia (imagem/vídeo)"
                value={form.url_midia || ''}
                onChange={(e) => setForm({ ...form, url_midia: e.target.value })}
              />
              <TextField
                fullWidth
                label="Thumbnail (URL)"
                value={form.thumbnail || ''}
                onChange={(e) => setForm({ ...form, thumbnail: e.target.value })}
              />
              {form.tipo === 'video' && (
                <TextField
                  fullWidth
                  type="number"
                  label="Duração (segundos)"
                  value={form.duracao_segundos ?? ''}
                  onChange={(e) => setForm({ ...form, duracao_segundos: e.target.value === '' ? undefined : Number(e.target.value) })}
                />
              )}
            </Box>

            {editing && editing.estado === 'publicado' && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
                  Métricas pós-publicação
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 3 }}>
                  <TextField fullWidth type="number" label="Views" value={form.views ?? 0} onChange={(e) => setForm({ ...form, views: Number(e.target.value) })} />
                  <TextField fullWidth type="number" label="Cliques" value={form.cliques ?? 0} onChange={(e) => setForm({ ...form, cliques: Number(e.target.value) })} />
                  <TextField fullWidth type="number" label="Engagement" value={form.engagement ?? 0} onChange={(e) => setForm({ ...form, engagement: Number(e.target.value) })} />
                </Box>
              </Box>
            )}

            <TextField
              fullWidth
              multiline
              minRows={2}
              label="Notas internas"
              value={form.notas || ''}
              onChange={(e) => setForm({ ...form, notas: e.target.value })}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={save} disabled={saving}>
            {saving ? 'A guardar…' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!toast} autoHideDuration={3500} onClose={() => setToast(null)}>
        {toast ? (
          <Alert severity={toast.severity} onClose={() => setToast(null)}>
            {toast.msg}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
}

// ícone Instagram mantido import p/ futuro uso
void InstagramIcon;
